/**
 * Platform maintenance, as the client learns it.
 *
 * The API has a maintenance mode (`MaintenanceGuard` + `AppConfig.maintenance_enabled`):
 * every path except a small allowlist answers 503 with the operator's own wording. Until
 * this file existed nothing on the client noticed. `ApiClientProvider` deliberately
 * suppresses 5xx bodies -- a framework 500 page leaks URLs and internals to the user --
 * so the operator's message was thrown away and a maintenance window looked exactly like
 * the server being down or the phone having no signal. Which is the outcome the guard's
 * own comment says it exists to avoid.
 *
 * So the state is discovered from the refusal itself, not polled: one 503 in the
 * maintenance shape sets it, and the next successful response of any kind clears it. No
 * timer, and nothing to get stuck -- when the window ends, the first request through
 * clears the flag as a side effect of working.
 *
 * Deliberately a plain module rather than a provider or a store:
 *   * `ApiClientProvider` is shared with busLineSrv and busLineWeb, which are browser
 *     bundles that must not pull `react-native` in (see CLAUDE.md). This file imports
 *     nothing at all, so it is safe from both sides.
 *   * the writer is a bare function inside a fetch handler, which has no hooks and no
 *     context. A subscription is the only thing that reaches a React tree from there.
 *
 * The console never sees any of this: `/admin` is on the guard's allowlist, so the one
 * client that has to keep working during a window is the one that turns it off.
 */

export interface MaintenanceState {
    /** The API is refusing traffic right now, and said so in the maintenance shape. */
    active: boolean;
    /** The operator's wording, from `AppConfig.maintenance_message`. */
    message: string | null;
    /** When this was last set, so a screen can say how long it has been going. */
    since: number | null;
}

type Listener = (state: MaintenanceState) => void;

let state: MaintenanceState = { active: false, message: null, since: null };
const listeners = new Set<Listener>();

/**
 * How many maintenance probes are in flight — see `runMaintenanceProbe`.
 *
 * A counter rather than a boolean because two screens could probe at once, and the
 * first one to finish must not lift the suppression the second is relying on.
 */
let probesInFlight = 0;

const emit = (): void => {
    // A copy per notification: a listener that stashes the object must not then see it
    // mutate under it on the next change.
    const snapshot: MaintenanceState = { ...state };
    listeners.forEach((listener) => {
        try {
            listener(snapshot);
        } catch {
            // A throwing subscriber must not break the fetch that triggered this, nor
            // stop the other subscribers from being told.
        }
    });
};

export const getMaintenanceState = (): MaintenanceState => ({ ...state });

/**
 * Called from the API client when a request is refused with 503 in the maintenance
 * shape. Re-notifies only when something actually changed -- during a window EVERY
 * request lands here, and re-rendering the tree per refused request would be a stampede.
 */
export const setMaintenanceActive = (message: string | null): void => {
    const text = message && message.trim() ? message.trim() : null;
    if (state.active && state.message === text) return;
    state = { active: true, message: text, since: state.since ?? Date.now() };
    emit();
};

/**
 * Called on any successful response: the platform is answering again.
 *
 * Ignored while a probe is in flight, and that exception is the whole reason
 * `runMaintenanceProbe` exists — see it for why.
 */
export const clearMaintenanceState = (): void => {
    if (probesInFlight > 0) return;
    if (!state.active) return;
    state = { active: false, message: null, since: null };
    emit();
};

/**
 * Run the "is the window over yet?" request without its own success ending the window.
 *
 * The probe asks `/check/version`, which is on MaintenanceGuard's allowlist and
 * therefore answers 200 THROUGHOUT a window — and a 200 clears the flag. So a bare
 * retry would tear the maintenance screen down before it had read the answer, drop the
 * user into an app where every request 503s, and let the screen slam back. What decides
 * here is the response BODY, so the reflex is suspended for the length of the call and
 * the caller sets the state from what the server actually said.
 *
 * Suppressing the clear for other requests that happen to overlap is harmless: during
 * a window they are being refused anyway, and if the window HAS ended the probe is
 * about to clear the flag itself.
 */
export const runMaintenanceProbe = async <T>(request: () => Promise<T>): Promise<T> => {
    probesInFlight += 1;
    try {
        return await request();
    } finally {
        probesInFlight -= 1;
    }
};

export const subscribeMaintenance = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/**
 * Is this failed response the API's maintenance mode?
 *
 * Strict on purpose. A 503 from a proxy, a load balancer or a restarting container is
 * also "service unavailable", and treating those as maintenance would show a window
 * that nobody opened -- with no message to show, since only our own guard sends one.
 * So all three have to hold: the status, a JSON object body, and a `message` that is
 * text rather than one of the API's numeric error codes.
 */
export const isMaintenanceResponse = (status: number, body: unknown): boolean => {
    if (status !== 503) return false;
    if (!body || typeof body !== 'object') return false;
    const message = (body as { message?: unknown }).message;
    if (typeof message !== 'string' || message.trim() === '') return false;
    return !/^\d+$/.test(message.trim());
};
